import React, { useState, useEffect, useRef } from 'react';

const RoutePreview = ({ route, onClose }) => {
    const [pos, setPos] = useState({ x: 20, y: 120 });
    const [isDragging, setIsDragging] = useState(false);
    const dragStart = useRef({ x: 0, y: 0 });
    const panelRef = useRef(null);

    // Initial position on mount (left side)
    useEffect(() => {
        setPos({ x: 20, y: 120 });
    }, []);

    const onMouseDown = (e) => {
        if (e.target.closest('.drag-handle')) {
            setIsDragging(true);
            dragStart.current = { x: e.clientX - pos.x, y: e.clientY - pos.y };
        }
    };

    useEffect(() => {
        const onMouseMove = (e) => {
            if (!isDragging) return;
            setPos({
                x: e.clientX - dragStart.current.x,
                y: e.clientY - dragStart.current.y
            });
        };
        const onMouseUp = () => setIsDragging(false);

        if (isDragging) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        }
        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging]);

    if (!route) return null;

    // Calculate bounds to scale preview
    const xs = route.points.map(p => p.x);
    const ys = route.points.map(p => p.y);
    const minPointX = Math.min(...xs, 0);
    const maxPointX = Math.max(...xs, 0);
    const minPointY = Math.min(...ys, 0);
    const maxPointY = Math.max(...ys, 0);

    const padding = 8;
    const width = Math.max(maxPointX - minPointX + padding * 2, 40);
    const height = Math.max(maxPointY - minPointY + padding * 2, 40);

    const pointsPath = route.points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x - minPointX + padding} ${p.y - minPointY + padding}`).join(' ');

    return (
        <div
            ref={panelRef}
            className="fixed z-[9999] select-none pointer-events-auto"
            style={{ left: pos.x, top: pos.y }}
            onMouseDown={onMouseDown}
        >
            <div className="bg-gray-900/90 border border-white/10 rounded-2xl shadow-2xl backdrop-blur-2xl ring-1 ring-white/5 w-56 overflow-hidden flex flex-col animate-in fade-in zoom-in-95 duration-300">
                {/* Header / Drag Handle */}
                <div className="drag-handle cursor-move bg-white/5 border-b border-white/10 px-4 py-2 flex items-center justify-between group">
                    <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-500 animate-pulse" />
                        <span className="text-[10px] text-gray-400 font-bold uppercase tracking-widest">Route Preview</span>
                    </div>
                    {onClose && (
                        <button
                            onClick={(e) => { e.stopPropagation(); onClose(); }}
                            className="text-gray-500 hover:text-white transition-colors"
                        >
                            <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    )}
                </div>

                {/* Content */}
                <div className="p-4 flex flex-col gap-4">
                    <div className="space-y-1">
                        <h3 className="text-sm font-black text-white leading-tight">{route.name}</h3>
                        <div className="flex gap-1 flex-wrap">
                            {route.tags?.map(t => (
                                <span key={t} className="text-[8px] text-blue-400/80 bg-blue-500/10 px-1.5 py-0.5 rounded-full border border-blue-500/10 lowercase">#{t}</span>
                            ))}
                        </div>
                    </div>

                    {/* SVG Preview Box */}
                    <div className="relative aspect-square flex items-center justify-center bg-black/40 rounded-xl border border-white/5 overflow-visible">
                        <svg
                            width="100%"
                            height="100%"
                            viewBox={`0 0 ${width} ${height}`}
                            className="p-4 overflow-visible"
                        >
                            {/* Grid Dots */}
                            <pattern id="grid-dots" width="10" height="10" patternUnits="userSpaceOnUse">
                                <circle cx="1" cy="1" r="0.5" fill="rgba(255,255,255,0.05)" />
                            </pattern>
                            <rect width="100%" height="100%" fill="url(#grid-dots)" />

                            {/* Target Marker for Zones */}
                            {route.tags?.includes('zone') && (
                                <ellipse
                                    cx={route.points[route.points.length - 1].x - minPointX + padding}
                                    cy={route.points[route.points.length - 1].y - minPointY + padding}
                                    rx="12" ry="9"
                                    fill="rgba(59, 130, 246, 0.15)"
                                    stroke="rgba(59, 130, 246, 0.5)"
                                    strokeWidth="0.8"
                                    strokeDasharray="2,2"
                                />
                            )}

                            {/* Origin Marker */}
                            <circle cx={-minPointX + padding} cy={-minPointY + padding} r="2.5" fill="#3b82f6" fillOpacity="0.8" />

                            {/* Route Line - 50% Thinner (1.25 instead of 2.5) */}
                            <path
                                d={pointsPath}
                                fill="none"
                                stroke="#fff"
                                strokeWidth="1.25"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                className="drop-shadow-[0_0_8px_rgba(255,255,255,0.3)]"
                            />

                            {/* Arrow Head - 50% Smaller */}
                            {route.points.length >= 2 && (() => {
                                const last = route.points[route.points.length - 1];
                                const prev = route.points[route.points.length - 2];
                                const angle = Math.atan2(last.y - prev.y, last.x - prev.x) * 180 / Math.PI;
                                return (
                                    <path
                                        d="M -1.75 -1.75 L 2.25 0 L -1.75 1.75 Z"
                                        fill="#fff"
                                        transform={`translate(${last.x - minPointX + padding}, ${last.y - minPointY + padding}) rotate(${angle})`}
                                    />
                                );
                            })()}
                        </svg>

                        {/* Shadow Gradient Surround */}
                        <div className="absolute inset-0 pointer-events-none rounded-xl shadow-[inset_0_0_20px_rgba(0,0,0,0.4)]" />
                    </div>

                    <div className="text-[9px] text-gray-500 italic text-center">
                        Drag concept to valid player to apply
                    </div>
                </div>
            </div>
        </div>
    );
};

export default RoutePreview;
